import {
  PublicRequestError,
  playerApi,
  type PlayerPushConfig,
  type PlayerPushSubscription,
} from '../api/public.ts'
import {
  closePlayerPushNotifications,
  createPlayerPushEnableIntent,
  decodeApplicationServerKey,
  defaultPlayerWebPushBrowser,
  ensurePlayerPushRegistration,
  ownedArrayBuffer,
  persistentDeviceID,
  playerPushRegistration,
  postRevisionAuthority,
  readPlayerPushEnableIntent,
  readStoredSession,
  removeLegacyStoredOwner,
  removePlayerPushEnableIntent,
  removeStoredSession,
  sameBytes,
  serializePushSubscription,
  writeStoredSession,
  writePlayerPushEnableIntent,
  type PlayerPushEnableIntent,
  type PlayerWebPushBrowser,
} from './playerWebPushBrowser.ts'

export {
  decodeApplicationServerKey,
  serializePushSubscription,
} from './playerWebPushBrowser.ts'

export type PlayerWebPushStatus =
  | 'checking'
  | 'signed_out'
  | 'unsupported'
  | 'unavailable'
  | 'off'
  | 'on'
  | 'blocked'
  | 'error'

export type PlayerWebPushState = {
  status: PlayerWebPushStatus
  active: boolean
  busy: boolean
  permission: NotificationPermission | 'unsupported'
  error: string | null
}

type PlayerWebPushAPI = {
  config: (signal?: AbortSignal) => Promise<PlayerPushConfig>
  put: (
    deviceID: string,
    subscription: PlayerPushSubscription,
    sessionToken: string,
    signal?: AbortSignal,
  ) => Promise<void>
  delete: (
    deviceID: string,
    sessionToken: string,
    signal?: AbortSignal,
  ) => Promise<void>
}

type PlayerWebPushOptions = {
  authenticated: () => boolean
  actorKey: () => string
  onState: (state: PlayerWebPushState) => void
  onChannelActive: (active: boolean) => void
  onOpen?: () => void
  api?: PlayerWebPushAPI
  browser?: PlayerWebPushBrowser
  permissionTimeoutMS?: number
  serviceWorkerTimeoutMS?: number
}

export type PlayerWebPushRuntime = {
  start: () => Promise<void>
  sync: () => Promise<void>
  enable: () => Promise<void>
  disable: (options?: { bestEffort?: boolean }) => Promise<void>
  dispose: () => void
}

const sessionTokenPattern = /^[0-9a-f]{16}$/
const revisionEpochPattern = /^[0-9a-f]{16}$/
const eventKeyPattern = /^(ready_check|match_connect)\.[0-9a-f]{16}$/
const lifecycleAPITimeoutMS = 3_000
const permissionRequestTimeoutMS = 30_000

type EnabledPlayerWebPushConfig = {
  enabled: true
  sessionToken: string
  applicationServerKey: Uint8Array
  revisionEpoch: string
  revisionFloor: number
}

type NormalizedPlayerWebPushConfig = EnabledPlayerWebPushConfig | {
  enabled: false
  sessionToken: string
}

export const initialPlayerWebPushState: PlayerWebPushState = {
  status: 'checking',
  active: false,
  busy: false,
  permission: 'unsupported',
  error: null,
}

export function createPlayerWebPushRuntime(
  options: PlayerWebPushOptions,
): PlayerWebPushRuntime {
  const api = options.api ?? {
    config: playerApi.pushConfig,
    put: playerApi.putPushSubscription,
    delete: playerApi.deletePushSubscription,
  }
  const browser = options.browser ?? defaultPlayerWebPushBrowser()
  let running = false
  let disposed = false
  let state = { ...initialPlayerWebPushState }
  let mutationTail: Promise<void> = Promise.resolve()
  let stopBrowserChanges: (() => void) | null = null
  let cachedConfig: EnabledPlayerWebPushConfig | null = null
  const permissionTimeoutMS = options.permissionTimeoutMS ?? permissionRequestTimeoutMS
  const serviceWorkerTimeoutMS = options.serviceWorkerTimeoutMS

  const handleMessage = (event: MessageEvent) => {
    if (!validPlayerPushOpenMessage(event.data) || !options.authenticated()) return
    options.onOpen?.()
  }
  const handleExternalChange = () => {
    if (!running || disposed) return
    publish({ status: 'checking', active: false, busy: true, error: null })
    void sync()
  }

  function publish(next: Partial<PlayerWebPushState>): void {
    state = { ...state, ...next }
    options.onChannelActive(state.active && state.status === 'on')
    options.onState({ ...state })
  }

  function enqueueLifecycle<T>(task: () => Promise<T>): Promise<T> {
    const predecessor = mutationTail
    // Reserve the origin-wide queue position at invocation time. If the lock
    // request waited behind the local predecessor instead, another tab could
    // enable a newer session before an already-requested logout cleanup.
    const result = browser.withLifecycleLock(async () => {
      await predecessor.catch(() => undefined)
      return await task()
    })
    mutationTail = result.then(() => undefined, () => undefined)
    return result
  }

  async function start(): Promise<void> {
    if (running || disposed) return
    running = true
    browser.addMessageListener(handleMessage)
    stopBrowserChanges = browser.subscribeChanges(handleExternalChange)
    await sync()
  }

  function sync(): Promise<void> {
    if (!running || disposed) return Promise.resolve()
    return enqueueLifecycle(syncNow)
  }

  function enable(): Promise<void> {
    if (!running || disposed || !options.authenticated() || !options.actorKey()) {
      return Promise.resolve()
    }
    if (!browser.supported()) {
      publish({
        status: 'unsupported',
        active: false,
        busy: false,
        permission: 'unsupported',
        error: null,
      })
      return Promise.resolve()
    }
    const config = cachedConfig
    if (!config) {
      publish({
        status: 'error',
        active: false,
        busy: false,
        permission: browser.permission(),
        error: 'Конфигурация Web Push ещё не загружена.',
      })
      return Promise.resolve()
    }
    // This call must happen directly in the click turn. Awaiting config or any
    // other network operation first loses transient user activation in Safari
    // and can make Chrome suppress the permission prompt.
    const permission = browser.permission() === 'granted'
      ? Promise.resolve<NotificationPermission>('granted')
      : browser.requestPermission()
    let intent: PlayerPushEnableIntent
    try {
      intent = createPlayerPushEnableIntent(browser, config.sessionToken)
    } catch (error) {
      publishPushError(false, error)
      return Promise.resolve()
    }
    publish({
      status: 'checking',
      active: false,
      busy: true,
      permission: browser.permission(),
      error: null,
    })
    const reservation = enqueueLifecycle(async () => {
      if (disposed || !running || !options.authenticated()) return false
      writePlayerPushEnableIntent(browser, intent)
      return true
    })
    return finishEnableIntent(intent, permission, reservation)
  }

  function disable(
    disableOptions: { bestEffort?: boolean } = {},
  ): Promise<void> {
    if (disposed) return Promise.resolve()
    const ownerToken = cachedConfig?.sessionToken ?? readStoredSession(browser)
    publish({ ...state, active: false, busy: true, error: null })
    const bestEffort = disableOptions.bestEffort === true
    const cleanup = enqueueLifecycle(() => disableNow(ownerToken, bestEffort))
    return bestEffort ? settleBestEffort(cleanup) : cleanup
  }

  function dispose(): void {
    if (disposed) return
    disposed = true
    if (running) browser.removeMessageListener(handleMessage)
    stopBrowserChanges?.()
    stopBrowserChanges = null
    running = false
    options.onChannelActive(false)
  }

  async function syncNow(): Promise<void> {
    if (disposed || !running) return
    removeLegacyStoredOwner(browser)
    if (!options.authenticated() || !options.actorKey()) {
      cachedConfig = null
      // Logout performs an owned, serialized cleanup before clearing auth.
      // A stale signed-out tab must not tear down an origin-global
      // subscription that a different account just enabled in another tab.
      publish({
        status: 'signed_out',
        active: false,
        busy: false,
        permission: browser.permission(),
        error: null,
      })
      return
    }
    if (!browser.supported()) {
      cachedConfig = null
      publish({
        status: 'unsupported',
        active: false,
        busy: false,
        permission: 'unsupported',
        error: null,
      })
      return
    }
    try {
      await syncLocked()
    } catch (error) {
      publish({
        status: 'error',
        active: false,
        busy: false,
        permission: browser.permission(),
        error: webPushErrorMessage(error),
      })
    }
  }

  async function syncLocked(): Promise<void> {
    if (disposed || !running || !options.authenticated()) return
    let registration: ServiceWorkerRegistration | null = null
    let subscription: PushSubscription | null = null
    let ownerToken = ''
    try {
      registration = await browser.registration()
      if (registration && !playerPushRegistration(registration)) {
        throw new Error('push_service_worker_conflict')
      }
      subscription = await registration?.pushManager.getSubscription() ?? null
      ownerToken = readStoredSession(browser)
    } catch (error) {
      publishPushError(false, error)
      return
    }

    let config: NormalizedPlayerWebPushConfig
    try {
      config = await loadAuthoritativeConfig()
    } catch (error) {
      // Preserve the local subscription and owner marker, but do not claim
      // that the server delivery channel is synchronized while config is
      // unavailable. This keeps the legacy browser visual as a fallback.
      publishPushError(false, error)
      return
    }
    if (disposed || !running || !options.authenticated()) return

    cachedConfig = config.enabled ? config : null
    if (config.enabled) postRevisionAuthority(registration)

    if (!subscription) {
      if (ownerToken) {
        removeStoredSession(browser, ownerToken)
        browser.broadcastChange()
      }
      publishPushInactive(config.enabled ? 'off' : 'unavailable')
      return
    }

    // A valid owner token may belong to a newer account in another tab. A
    // passive reconcile never mutates a foreign origin-global subscription.
    if (ownerToken !== config.sessionToken) {
      publishPushInactive(config.enabled ? 'off' : 'unavailable')
      return
    }

    if (!config.enabled) {
      await revokeOwnedSubscription(subscription, registration, ownerToken)
      publishPushInactive('unavailable')
      return
    }
    if (!sameBytes(
      subscription.options.applicationServerKey,
      config.applicationServerKey,
    )) {
      await revokeOwnedSubscription(subscription, registration, ownerToken)
      publishPushInactive('off')
      return
    }

    try {
      await abortableAPIRequest(signal => api.put(
        persistentDeviceID(browser),
        serializePushSubscription(subscription),
        config.sessionToken,
        signal,
      ))
    } catch (error) {
      if (playerPushSessionConflict(error)) {
        resetSessionAuthority(ownerToken)
        return
      }
      if (permanentPushSubscriptionError(error)) {
        await revokeOwnedSubscription(subscription, registration, ownerToken)
        publishPushInactive('off', error)
        return
      }
      publishPushError(false, error)
      return
    }
    if (readStoredSession(browser) !== ownerToken) return
    publish({
      status: 'on',
      active: true,
      busy: false,
      permission: browser.permission(),
      error: null,
    })
  }

  async function finishEnableIntent(
    intent: PlayerPushEnableIntent,
    permissionRequest: Promise<NotificationPermission>,
    reservation: Promise<boolean>,
  ): Promise<void> {
    let reserved: boolean
    try {
      reserved = await reservation
    } catch (error) {
      publishPushError(false, error)
      return
    }
    if (!reserved) return

    let permission: NotificationPermission
    let permissionFailure: unknown = null
    try {
      permission = await boundedPromise(permissionRequest, permissionTimeoutMS)
    } catch (error) {
      permission = browser.permission() === 'denied' ? 'denied' : 'default'
      permissionFailure = error
    }
    return enqueueLifecycle(() => finalizeEnableIntent(
      intent,
      permission,
      permissionFailure,
    ))
  }

  async function finalizeEnableIntent(
    intent: PlayerPushEnableIntent,
    permission: NotificationPermission,
    permissionFailure: unknown,
  ): Promise<void> {
    const currentIntent = readPlayerPushEnableIntent(browser)
    if (
      !currentIntent ||
      currentIntent.id !== intent.id ||
      currentIntent.sessionToken !== intent.sessionToken
    ) return

    if (permissionFailure) {
      removePlayerPushEnableIntent(browser, intent)
      if (!disposed) publishPushError(false, permissionFailure)
      return
    }
    if (permission !== 'granted') {
      removePlayerPushEnableIntent(browser, intent)
      if (!disposed) {
        publish({
          status: permission === 'denied' ? 'blocked' : 'off',
          active: false,
          busy: false,
          permission,
          error: null,
        })
      }
      return
    }
    if (disposed || !running || !options.authenticated()) {
      removePlayerPushEnableIntent(browser, intent)
      return
    }

    let authority: NormalizedPlayerWebPushConfig
    try {
      authority = await loadAuthoritativeConfig()
    } catch (error) {
      removePlayerPushEnableIntent(browser, intent)
      publishPushError(false, error)
      return
    }
    if (authority.sessionToken !== intent.sessionToken) {
      removePlayerPushEnableIntent(browser, intent)
      resetSessionAuthority(intent.sessionToken)
      return
    }
    if (!authority.enabled) {
      removePlayerPushEnableIntent(browser, intent)
      cachedConfig = null
      publishPushInactive('unavailable')
      return
    }

    removePlayerPushEnableIntent(browser, intent)
    cachedConfig = authority
    await enableLocked(permission, authority)
  }

  async function enableLocked(
    permission: NotificationPermission,
    authority: EnabledPlayerWebPushConfig,
  ): Promise<void> {
    let candidate: PushSubscription | null = null
    let registration: ServiceWorkerRegistration | null = null
    let deviceID = ''
    let previouslySynced = false
    const config = authority
    try {
      if (disposed || !options.authenticated()) return

      registration = await ensurePlayerPushRegistration(browser, serviceWorkerTimeoutMS)
      candidate = await registration.pushManager.getSubscription()
      const previousOwner = readStoredSession(browser)
      previouslySynced = Boolean(
        candidate && previousOwner === config.sessionToken,
      )
      if (candidate && !previouslySynced) {
        try {
          await candidate.unsubscribe()
        } catch (error) {
          await closePlayerPushNotifications(registration)
          throw error
        }
        await closePlayerPushNotifications(registration)
        candidate = null
        removeStoredSession(browser, previousOwner)
        browser.broadcastChange()
      } else if (!candidate && previousOwner) {
        removeStoredSession(browser, previousOwner)
        browser.broadcastChange()
      }
      if (
        candidate &&
        !sameBytes(
          candidate.options.applicationServerKey,
          config.applicationServerKey,
        )
      ) {
        const revoked = await revokeOwnedSubscription(
          candidate,
          registration,
          config.sessionToken,
        )
        if (!revoked.revoked) return
        candidate = null
        previouslySynced = false
      }
      if (!candidate) {
        candidate = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: ownedArrayBuffer(config.applicationServerKey),
        })
      }
      if (disposed || !options.authenticated()) {
        throw new Error('push_actor_changed')
      }
      const synchronizedCandidate = candidate
      if (!synchronizedCandidate) {
        throw new Error('push_subscription_invalid')
      }

      deviceID = persistentDeviceID(browser)
      await abortableAPIRequest(signal => api.put(
        deviceID,
        serializePushSubscription(synchronizedCandidate),
        config.sessionToken,
        signal,
      ))
      if (disposed || !options.authenticated()) throw new Error('push_actor_changed')
      writeStoredSession(browser, config.sessionToken)
      postRevisionAuthority(registration)
      browser.broadcastChange()
      publish({
        status: 'on',
        active: true,
        busy: false,
        permission,
        error: null,
      })
    } catch (error) {
      if (playerPushSessionConflict(error)) {
        resetSessionAuthority(config.sessionToken)
        return
      }
      if (!previouslySynced) {
        if (deviceID) {
          const cleanupFailure = await abortableAPIRequest(
            signal => api.delete(deviceID, config.sessionToken, signal),
            1_000,
          ).then(() => null, cleanupError => cleanupError)
          if (playerPushSessionConflict(cleanupFailure)) {
            resetSessionAuthority(config.sessionToken)
            return
          }
        }
        await candidate?.unsubscribe().catch(() => false)
        await closePlayerPushNotifications(registration)
        removeStoredSession(browser, config.sessionToken)
        if (candidate) browser.broadcastChange()
      }
      if (!disposed) {
        publish({
          status: 'error',
          active: false,
          busy: false,
          permission: browser.permission(),
          error: webPushErrorMessage(error),
        })
      }
    }
  }

  async function disableNow(
    ownerToken: string,
    bestEffort: boolean,
  ): Promise<void> {
    if (ownerToken) {
      removePlayerPushEnableIntent(browser, { sessionToken: ownerToken })
    }
    try {
      await disableLocked(ownerToken, bestEffort)
    } catch (error) {
      publishPushInactive('off', bestEffort ? null : error)
    }
  }

  async function disableLocked(
    ownerToken: string,
    bestEffort: boolean,
  ): Promise<void> {
    let registration: ServiceWorkerRegistration | null = null
    let subscription: PushSubscription | null = null
    let failure: unknown
    let sessionConflict = false
    try {
      registration = await browser.registration()
      if (registration && !playerPushRegistration(registration)) registration = null
      subscription = await registration?.pushManager.getSubscription() ?? null
    } catch (error) {
      failure = error
    }

    if (ownerToken && readStoredSession(browser) === ownerToken) {
      try {
        const config = await loadAuthoritativeConfig()
        if (config.sessionToken === ownerToken) {
          await abortableAPIRequest(
            signal => api.delete(persistentDeviceID(browser), ownerToken, signal),
            bestEffort ? 1_000 : lifecycleAPITimeoutMS,
          )
        } else {
          sessionConflict = true
        }
      } catch (error) {
        if (playerPushSessionConflict(error)) sessionConflict = true
        failure ??= error
      }
    }
    if (sessionConflict) {
      resetSessionAuthority(ownerToken)
      return
    }
    if (ownerToken && readStoredSession(browser) === ownerToken) {
      try {
        await subscription?.unsubscribe()
      } catch (error) {
        failure ??= error
      }
      await closePlayerPushNotifications(registration)
      removeStoredSession(browser, ownerToken)
      browser.broadcastChange()
    }
    cachedConfig = bestEffort ? null : cachedConfig
    publishPushInactive('off', failure && !bestEffort ? failure : null)
  }

  async function revokeOwnedSubscription(
    subscription: PushSubscription,
    registration: ServiceWorkerRegistration | null,
    ownerToken: string,
  ): Promise<{ revoked: boolean; failure: unknown }> {
    let failure: unknown
    if (readStoredSession(browser) === ownerToken) {
      try {
        await abortableAPIRequest(signal => api.delete(
          persistentDeviceID(browser),
          ownerToken,
          signal,
        ))
      } catch (error) {
        if (playerPushSessionConflict(error)) {
          resetSessionAuthority(ownerToken)
          return { revoked: false, failure: error }
        }
        failure = error
      }
    }
    if (readStoredSession(browser) !== ownerToken) {
      return { revoked: false, failure }
    }
    await subscription.unsubscribe().catch(() => false)
    await closePlayerPushNotifications(registration)
    removeStoredSession(browser, ownerToken)
    browser.broadcastChange()
    return { revoked: true, failure }
  }

  async function loadAuthoritativeConfig(): Promise<NormalizedPlayerWebPushConfig> {
    return normalizePlayerWebPushConfig(
      await abortableAPIRequest(signal => api.config(signal)),
    )
  }

  function publishPushInactive(
    status: 'off' | 'unavailable',
    error: unknown = null,
  ): void {
    publish({
      status: status === 'off' && browser.permission() === 'denied'
        ? 'blocked'
        : status,
      active: false,
      busy: false,
      permission: browser.permission(),
      error: error ? webPushErrorMessage(error) : null,
    })
  }

  function publishPushError(active: boolean, error: unknown): void {
    publish({
      status: active ? 'on' : 'error',
      active,
      busy: false,
      permission: browser.permission(),
      error: webPushErrorMessage(error),
    })
  }

  function resetSessionAuthority(expectedToken: string): void {
    cachedConfig = null
    removeStoredSession(browser, expectedToken)
    browser.broadcastChange()
    publishPushInactive('off')
    if (running && !disposed) void sync()
  }

  return { start, sync, enable, disable, dispose }
}

function normalizePlayerWebPushConfig(
  config: PlayerPushConfig,
): NormalizedPlayerWebPushConfig {
  if (
    typeof config.session_token !== 'string' ||
    !sessionTokenPattern.test(config.session_token)
  ) {
    throw new Error('push_config_invalid')
  }
  if (config.enabled !== true) {
    return { enabled: false, sessionToken: config.session_token }
  }
  if (
    typeof config.revision_epoch !== 'string' ||
    !revisionEpochPattern.test(config.revision_epoch) ||
    !Number.isSafeInteger(config.revision_floor) ||
    Number(config.revision_floor) < 1
  ) {
    throw new Error('push_config_invalid')
  }
  return {
    enabled: true,
    sessionToken: config.session_token,
    applicationServerKey: decodeApplicationServerKey(
      config.application_server_key,
    ),
    revisionEpoch: config.revision_epoch,
    revisionFloor: Number(config.revision_floor),
  }
}

async function abortableAPIRequest<T>(
  request: (signal: AbortSignal) => Promise<T>,
  timeoutMS = lifecycleAPITimeoutMS,
): Promise<T> {
  const controller = new AbortController()
  let timeout: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<never>((_resolve, reject) => {
    timeout = setTimeout(() => {
      controller.abort()
      const error = new Error('push_api_timeout')
      error.name = 'AbortError'
      reject(error)
    }, timeoutMS)
  })
  try {
    return await Promise.race([
      Promise.resolve().then(() => request(controller.signal)),
      deadline,
    ])
  } finally {
    if (timeout !== undefined) clearTimeout(timeout)
  }
}

async function boundedPromise<T>(
  promise: Promise<T>,
  timeoutMS: number,
): Promise<T> {
  let timeout: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<never>((_resolve, reject) => {
    timeout = setTimeout(() => {
      const error = new Error('push_permission_timeout')
      error.name = 'AbortError'
      reject(error)
    }, Math.max(1, timeoutMS))
  })
  try {
    return await Promise.race([promise, deadline])
  } finally {
    if (timeout !== undefined) clearTimeout(timeout)
  }
}

async function settleBestEffort(
  promise: Promise<unknown>,
  timeoutMS = 1_000,
): Promise<void> {
  let timeout: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      promise.catch(() => undefined),
      new Promise<void>(resolve => {
        timeout = setTimeout(resolve, timeoutMS)
      }),
    ])
  } finally {
    if (timeout !== undefined) clearTimeout(timeout)
  }
}

function validPlayerPushOpenMessage(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const message = value as Record<string, unknown>
  return Object.keys(message).sort().join(',') ===
      'event_key,event_type,revision,type' &&
    message.type === 'dota688.player-push-open' &&
    (message.event_type === 'ready_check' || message.event_type === 'match_connect') &&
    typeof message.event_key === 'string' &&
    eventKeyPattern.test(message.event_key) &&
    message.event_key.startsWith(`${message.event_type}.`) &&
    Number.isSafeInteger(message.revision) &&
    Number(message.revision) > 0
}

function webPushErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    if (error.name === 'NotAllowedError') return 'Браузер запретил уведомления.'
    if (error.name === 'AbortError') return 'Операция с Web Push была прервана.'
    if (error.message === 'push_service_worker_conflict') {
      return 'Другой service worker уже использует эту область сайта.'
    }
  }
  return 'Не удалось синхронизировать Web Push. Повторите позже.'
}

function permanentPushSubscriptionError(error: unknown): boolean {
  return error instanceof PublicRequestError &&
    (error.status === 400 || error.status === 404 || error.status === 410)
}

function playerPushSessionConflict(error: unknown): boolean {
  return error instanceof PublicRequestError && error.status === 409
}
